<template lang="pug">
div
  v-row.sysgrao-row
    v-col(
      v-for="card in summaryCards",
      :key="card.key",
      cols="12",
      md="4"
    )
      summary-card(
        :title="card.title",
        :value="formatNumber(card.value)",
        :variant="card.variant"
      )

  v-row.sysgrao-row
    v-col(cols="12", md="6")
      v-card.panel-card(outlined)
        .panel-card__header
          span.panel-card__title Clientes (Bases de dados)
          v-btn.panel-card__expand(
            outlined,
            small,
            class="sysgrao-action-button",
            @click="dialogs.clientes = true"
          )
            v-icon(small) mdi-arrow-expand

        v-card-text.panel-card__content
          apexchart(
            v-if="rankingPreviewSeries.length",
            class="sysgrao-ranking-chart",
            type="bar",
            :options="rankingChartOptions",
            :series="rankingPreviewSeries",
            height="100%"
          )
          .panel-card__empty(v-else)
            span Nenhum dado encontrado para o período selecionado.

    v-col(cols="12", md="6")
      v-card.panel-card(outlined)
        .panel-card__header
          span.panel-card__title Romaneios enviados (por período)
          v-btn.panel-card__expand(
            outlined,
            small,
            class="sysgrao-action-button",
            @click="dialogs.periodo = true"
          )
            v-icon(small) mdi-arrow-expand

        v-card-text.panel-card__content
          apexchart(
            v-if="periodSeries.length",
            :key="periodChartRenderKey",
            class="sysgrao-period-chart",
            type="line",
            :options="periodChartOptions",
            :series="periodSeries",
            height="100%"
          )
          .panel-card__empty(v-else)
            span Nenhum dado encontrado para o período selecionado.

  v-dialog(
    :value="dialogs.clientes",
    @input="dialogs.clientes = $event",
    content-class="sysgrao-dialog-content",
    max-width="1200px",
    width="1200px"
  )
    v-card.panel-dialog
      .panel-card__header.panel-card__header--dialog
        span.panel-card__title Clientes (Bases de dados)
        v-btn(icon, @click="dialogs.clientes = false")
          v-icon mdi-close
      v-card-text.panel-card__dialog-content.panel-card__dialog-content--scroll
        apexchart(
          v-if="rankingDialogSeries.length",
          class="sysgrao-ranking-chart",
          type="bar",
          :options="rankingDialogOptions",
          :series="rankingDialogSeries",
          :height="rankingDialogChartHeight"
        )
        .panel-card__empty(v-else)
          span Nenhum dado encontrado para o período selecionado.

  v-dialog(
    :value="dialogs.periodo",
    @input="dialogs.periodo = $event",
    content-class="sysgrao-dialog-content",
    max-width="1200px",
    width="1200px"
  )
    v-card.panel-dialog
      .panel-card__header.panel-card__header--dialog
        span.panel-card__title Romaneios enviados (por período)
        v-btn(icon, @click="dialogs.periodo = false")
          v-icon mdi-close
      v-card-text.panel-card__dialog-content.panel-card__dialog-content--scroll
        apexchart(
          v-if="periodSeries.length",
          :key="periodDialogRenderKey",
          class="sysgrao-period-chart",
          type="line",
          :options="periodDialogOptions",
          :series="periodSeries",
          height="520"
        )
        .panel-card__empty(v-else)
          span Nenhum dado encontrado para o período selecionado.
</template>

<script>
import SummaryCard from "@/components/dashboards/shared/SummaryCard";

const CHART_COLORS = [
  "#bf5b0a",
  "#ef6c00",
  "#e53935",
  "#c2185b",
  "#ad1457",
  "#b26f00",
  "#558b0f",
  "#1e8e3e",
  "#23713d",
  "#8d3c12"
];
const UNSELECTED_BAR_COLOR = "#dbe3ec";
const TOTAL_PERIOD_COLOR = "#2962ff";
const INITIAL_RANKING_LIMIT = 10;
const RANKING_DIALOG_MIN_HEIGHT = 520;
const RANKING_DIALOG_BAR_HEIGHT = 44;
const NUMBER_FORMATTERS = {
  integer: new Intl.NumberFormat("pt-BR"),
  decimal: new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })
};

function toNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function toArray(value) {
  return Array.isArray(value) ? value : [];
}

function createLocalDate(value) {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day);
}

function formatDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function shiftDays(date, amount) {
  const shifted = new Date(date);
  shifted.setDate(shifted.getDate() + amount);
  return shifted;
}

function normalizeDateValue(value) {
  if (!value) return "";
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed.match(/^\d{4}-\d{2}-\d{2}$/)) return trimmed;
    const slice = trimmed.slice(0, 10);
    if (slice.match(/^\d{4}-\d{2}-\d{2}$/)) return slice;
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? "" : formatDate(parsed);
}

function isCompleteDateValue(value) {
  return typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);
}

function createSummaryCards(totais = {}) {
  return [
    {
      key: "total-romaneios",
      title: "Total de romaneios enviados",
      value: toNumber(totais.romaneios),
      variant: "highlight"
    },
    {
      key: "total-clientes",
      title: "Clientes (Bases de dados)",
      value: toNumber(totais.clientes),
      variant: ""
    },
    {
      key: "total-produtores",
      title: "Produtores",
      value: toNumber(totais.produtores),
      variant: ""
    }
  ];
}

function getSortedRankingItems(romaneiosPorCliente) {
  return toArray(romaneiosPorCliente)
    .filter(item => item.nome)
    .sort((a, b) => b.romaneios - a.romaneios);
}

function buildTimelineData(periodItems, startDate, endDate) {
  if (!isCompleteDateValue(startDate) || !isCompleteDateValue(endDate)) return [];

  const grouped = toArray(periodItems).reduce((acc, item) => {
    const date = normalizeDateValue(item?.dtLancRomaneio);
    if (date) acc[date] = toNumber(item?.romaneios);
    return acc;
  }, {});
  const timeline = [];
  let current = createLocalDate(startDate);
  const final = createLocalDate(endDate);

  if (Number.isNaN(current.getTime()) || Number.isNaN(final.getTime()) || current > final) {
    return [];
  }

  while (current <= final) {
    const iso = formatDate(current);
    timeline.push({ x: `${iso}T12:00:00`, y: grouped[iso] || 0 });
    current = shiftDays(current, 1);
  }
  return timeline;
}

function buildRankingState(items) {
  const normalizedItems = toArray(items);
  return {
    clientMap: normalizedItems.reduce((acc, item) => {
      acc[item.nome] = item;
      return acc;
    }, {}),
    categories: normalizedItems.map(item => item.nome),
    series: normalizedItems.length
      ? [{ name: "Romaneios", data: normalizedItems.map(item => item.romaneios) }]
      : []
  };
}

function buildPeriodSeries(romaneiosPorPeriodo, startDate, endDate, ranking, selectedClient) {
  if (!isCompleteDateValue(startDate) || !isCompleteDateValue(endDate)) return [];

  const totalData = buildTimelineData(romaneiosPorPeriodo, startDate, endDate);
  const client = selectedClient ? ranking.clientMap[selectedClient] : null;
  const clientSeries = client
    ? [
        {
          name: selectedClient,
          color: CHART_COLORS[ranking.categories.indexOf(selectedClient) % CHART_COLORS.length],
          data: buildTimelineData(client.romaneiosPorPeriodo, startDate, endDate)
        }
      ]
    : [];
  if (!toArray(romaneiosPorPeriodo).length && !clientSeries.length) return [];
  return [
    { name: "Romaneios enviados", color: TOTAL_PERIOD_COLOR, data: totalData },
    ...clientSeries
  ];
}

function createRankingChartOptions({
  chartHeight,
  categories,
  colors,
  onDataPointSelect,
  formatInteger,
  isMobile
}) {
  return {
    chart: {
      type: "bar",
      height: chartHeight,
      parentHeightOffset: 0,
      events: {
        dataPointSelection(event, chartContext, config) {
          onDataPointSelect(config.dataPointIndex);
        }
      },
      toolbar: { show: false }
    },
    colors,
    plotOptions: {
      bar: { horizontal: true, distributed: true, borderRadius: 4, barHeight: "72%" }
    },
    states: {
      active: { allowMultipleDataPointsSelection: true, filter: { type: "none" } },
      hover: { filter: { type: "darken", value: 0.08 } }
    },
    dataLabels: {
      enabled: !isMobile,
      formatter: value => formatInteger(value),
      style: {
        colors,
        fontSize: "12px",
        fontWeight: 600
      },
      background: {
        enabled: true,
        foreColor: "#fff",
        borderRadius: 4,
        padding: 10,
        opacity: 0.8,
        borderWidth: 1,
        borderColor: "#d4dce8"
      }
    },
    grid: {
      borderColor: "#dfe7f5",
      strokeDashArray: 4,
      padding: isMobile ? { left: 0, right: 0 } : {}
    },
    legend: { show: false },
    xaxis: {
      categories,
      axisBorder: { show: true },
      axisTicks: { show: true },
      labels: {
        show: !isMobile,
        style: { colors: "#5f6b7a", fontSize: "12px" },
        formatter: value => formatInteger(value)
      }
    },
    yaxis: {
      crosshairs: { show: true },
      tooltip: { enabled: false },
      labels: {
        show: !isMobile,
        maxWidth: 260,
        style: { colors: "#3d4b63", fontSize: "12px" }
      }
    },
    tooltip: {
      enabled: true,
      intersect: false,
      followCursor: false,
      y: {
        formatter: value => `${formatInteger(value)} romaneios`
      }
    }
  };
}

function createPeriodChartOptions({
  chartHeight,
  series,
  tickAmount,
  formatTimestampBR,
  formatInteger,
  animationsEnabled
}) {
  return {
    chart: {
      type: "line",
      height: chartHeight,
      parentHeightOffset: 0,
      toolbar: { show: false },
      zoom: { enabled: false },
      animations: { enabled: animationsEnabled }
    },
    colors: series.map(s => s.color),
    stroke: {
      curve: "monotoneCubic",
      width: series.map((s, i) => (i === 0 ? 2.5 : 2)),
      dashArray: series.map((s, i) => (i === 0 ? 0 : 2))
    },
    markers: {
      size: 0,
      strokeWidth: 0,
      hover: { size: 0 }
    },
    grid: {
      borderColor: "#dfe7f5",
      strokeDashArray: 4,
      padding: { bottom: 12 }
    },
    dataLabels: { enabled: false },
    legend: { show: series.length > 1, position: "top", horizontalAlign: "left" },
    xaxis: {
      type: "datetime",
      tickAmount,
      axisBorder: { show: true },
      axisTicks: { show: true },
      crosshairs: { show: true },
      tooltip: { enabled: false },
      labels: {
        rotate: 0,
        datetimeUTC: false,
        formatter: value => formatTimestampBR(value),
        style: { colors: "#5f6b7a", fontSize: "12px" }
      }
    },
    yaxis: {
      min: 0,
      forceNiceScale: true,
      labels: {
        formatter: value => formatInteger(value),
        style: { colors: "#5f6b7a", fontSize: "12px" }
      }
    },
    tooltip: {
      enabled: true,
      shared: true,
      intersect: false,
      followCursor: false,
      x: {
        show: true,
        formatter: value => formatTimestampBR(value)
      },
      y: {
        formatter: value => `${formatInteger(value)} romaneios`
      }
    }
  };
}

export default {
  name: "RomaneiosTab",

  components: {
    SummaryCard
  },

  props: {
    isActive: { type: Boolean, default: false },
    totais: { type: Object, default: () => ({ romaneios: 0, clientes: 0, produtores: 0 }) },
    romaneiosPorCliente: { type: Array, default: () => [] },
    romaneiosPorPeriodo: { type: Array, default: () => [] },
    dataInicial: { type: String, default: "" },
    dataFinal: { type: String, default: "" }
  },

  data() {
    return {
      periodChartRefreshTick: 0,
      selectedRankingClient: null,
      shouldAnimatePeriodChart: true,
      dialogs: { clientes: false, periodo: false }
    };
  },

  computed: {
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },

    summaryCards() {
      return createSummaryCards(this.totais);
    },

    rankingItems() {
      return getSortedRankingItems(this.romaneiosPorCliente);
    },

    rankingPreview() {
      return buildRankingState(this.rankingItems.slice(0, INITIAL_RANKING_LIMIT));
    },

    rankingDialog() {
      return buildRankingState(this.rankingItems);
    },

    rankingCategories() {
      return this.rankingPreview.categories;
    },

    rankingPreviewSeries() {
      return this.rankingPreview.series;
    },

    rankingDialogCategories() {
      return this.rankingDialog.categories;
    },

    rankingDialogSeries() {
      return this.rankingDialog.series;
    },

    rankingDialogChartHeight() {
      return Math.max(
        RANKING_DIALOG_MIN_HEIGHT,
        this.rankingDialogCategories.length * RANKING_DIALOG_BAR_HEIGHT
      );
    },

    periodSeries() {
      if (!isCompleteDateValue(this.dataInicial) || !isCompleteDateValue(this.dataFinal)) {
        return [];
      }

      return buildPeriodSeries(
        this.romaneiosPorPeriodo,
        this.dataInicial,
        this.dataFinal,
        this.rankingDialog,
        this.selectedRankingClient
      );
    },

    rankingBarColors() {
      if (!this.rankingCategories.length) return CHART_COLORS;
      if (
        !this.selectedRankingClient ||
        !this.rankingCategories.includes(this.selectedRankingClient)
      ) {
        return this.rankingCategories.map((_, i) => CHART_COLORS[i % CHART_COLORS.length]);
      }
      return this.rankingCategories.map((name, i) =>
        name === this.selectedRankingClient
          ? CHART_COLORS[i % CHART_COLORS.length]
          : UNSELECTED_BAR_COLOR
      );
    },

    rankingDialogBarColors() {
      if (!this.rankingDialogCategories.length) return CHART_COLORS;
      if (!this.selectedRankingClient) {
        return this.rankingDialogCategories.map((_, i) => CHART_COLORS[i % CHART_COLORS.length]);
      }
      return this.rankingDialogCategories.map((name, i) =>
        name === this.selectedRankingClient
          ? CHART_COLORS[i % CHART_COLORS.length]
          : UNSELECTED_BAR_COLOR
      );
    },

    rankingChartOptions() {
      return createRankingChartOptions({
        chartHeight: "100%",
        categories: this.rankingCategories,
        colors: this.rankingBarColors,
        onDataPointSelect: dataPointIndex =>
          this.toggleRankingClient(dataPointIndex, this.rankingCategories),
        formatInteger: v => this.formatNumber(v),
        isMobile: this.isMobile
      });
    },

    rankingDialogOptions() {
      return createRankingChartOptions({
        chartHeight: this.rankingDialogChartHeight,
        categories: this.rankingDialogCategories,
        colors: this.rankingDialogBarColors,
        onDataPointSelect: dataPointIndex =>
          this.toggleRankingClient(dataPointIndex, this.rankingDialogCategories),
        formatInteger: v => this.formatNumber(v),
        isMobile: this.isMobile
      });
    },

    periodTickAmount() {
      const length = toArray(this.periodSeries[0] && this.periodSeries[0].data).length;
      return Math.min(Math.max(1, length - 1), 8);
    },

    periodChartOptions() {
      return createPeriodChartOptions({
        chartHeight: "100%",
        series: this.periodSeries,
        tickAmount: this.periodTickAmount,
        formatTimestampBR: this.formatTimestampBR,
        formatInteger: v => this.formatNumber(v),
        animationsEnabled: this.shouldAnimatePeriodChart
      });
    },

    periodChartRenderKey() {
      return [
        "preview",
        this.periodChartRefreshTick,
        this.dataInicial,
        this.dataFinal,
        ...this.periodSeries.map(series => series.name)
      ].join("|");
    },

    periodDialogOptions() {
      return createPeriodChartOptions({
        chartHeight: 520,
        series: this.periodSeries,
        tickAmount: this.periodTickAmount,
        formatTimestampBR: this.formatTimestampBR,
        formatInteger: v => this.formatNumber(v),
        animationsEnabled: this.shouldAnimatePeriodChart
      });
    },

    periodDialogRenderKey() {
      return [
        "dialog",
        this.periodChartRefreshTick,
        this.dataInicial,
        this.dataFinal,
        ...this.periodSeries.map(series => series.name)
      ].join("|");
    }
  },

  methods: {
    resetSelection() {
      this.selectedRankingClient = null;
    },

    refreshPeriodCharts() {
      this.shouldAnimatePeriodChart = true;
      this.periodChartRefreshTick += 1;
    },

    formatNumber(value) {
      const n = toNumber(value);
      return NUMBER_FORMATTERS.integer.format(Math.round(n));
    },

    formatTimestampBR(value) {
      const date = normalizeDateValue(value);
      if (!date) return "";
      const [year, month, day] = date.split("-");
      return `${day}/${month}/${year}`;
    },

    toggleRankingClient(dataPointIndex, categories = []) {
      const name = categories[dataPointIndex];
      if (!name) return;
      this.shouldAnimatePeriodChart = false;
      this.selectedRankingClient = this.selectedRankingClient === name ? null : name;
    }
  },

  watch: {
    isActive(value) {
      if (!value) return;
      this.$nextTick(() => {
        this.refreshPeriodCharts();
      });
    },

    romaneiosPorPeriodo() {
      this.$nextTick(() => {
        this.refreshPeriodCharts();
      });
    },

    romaneiosPorCliente() {
      if (!this.rankingDialogCategories.includes(this.selectedRankingClient)) {
        this.selectedRankingClient = null;
      }
    }
  },

  mounted() {
    if (this.isActive) {
      this.$nextTick(() => {
        this.refreshPeriodCharts();
      });
    }
  }
};
</script>

<style scoped>
.sysgrao-row {
  margin-left: -6px !important;
  margin-right: -6px !important;
}

.sysgrao-row > [class*="col-"] {
  padding: 6px !important;
}

.panel-card {
  border-color: #d9e2ef !important;
  box-shadow: 0 2px 6px rgba(15, 42, 77, 0.08);
  border-radius: 18px 6px 18px 6px !important;
}

.panel-dialog {
  border-color: #d9e2ef !important;
  border-radius: 10px !important;
  box-shadow: 0 2px 6px rgba(15, 42, 77, 0.08);
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 100%;
  overflow: hidden;
}

.panel-card {
  display: flex;
  flex-direction: column;
  min-height: 490px;
  height: 490px;
  max-height: 490px;
}

.panel-card__header {
  align-items: center;
  display: flex;
  justify-content: space-between;
  padding: 16px 16px 0;
}

.panel-card__header--dialog {
  flex: 0 0 auto;
  padding: 16px;
}

.panel-card__title {
  color: #0a1f44;
  font-size: 16px;
  font-weight: 700;
}

.panel-card__expand {
  height: 30px !important;
  min-width: 36px !important;
  padding: 0 8px !important;
}

.panel-card__content {
  flex: 1 1 0;
  min-height: 0;
  padding-bottom: 16px !important;
}

.panel-card__dialog-content {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.panel-card__dialog-content--scroll {
  flex: 1 1 auto;
  min-height: 0;
  max-height: none;
  overflow-y: auto;
}

::v-deep .sysgrao-dialog-content .v-dialog {
  margin: 24px auto;
  height: calc(100vh - 48px);
  max-height: calc(100vh - 48px);
}

.panel-card__empty {
  align-items: center;
  color: #5f6b7a;
  display: flex;
  height: 100%;
  justify-content: center;
  min-height: 320px;
  text-align: center;
}

.sysgrao-action-button {
  border-color: #c7d1dc !important;
  box-shadow: none !important;
  color: #6f7d8c !important;
  font-size: 11px !important;
  font-weight: 500;
  letter-spacing: 0.01em !important;
  text-transform: none !important;
}

.sysgrao-action-button::before {
  background-color: transparent !important;
}

.sysgrao-action-button .v-icon {
  color: #748291 !important;
}

.sysgrao-ranking-chart ::v-deep .apexcharts-bar-area,
.sysgrao-ranking-chart ::v-deep .apexcharts-yaxis text {
  cursor: pointer;
}

@media (max-width: 959px) {
  .panel-card__header {
    align-items: flex-start;
    flex-direction: column;
    gap: 12px;
  }

  .panel-card__expand {
    align-self: flex-end;
  }
}
</style>
